import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/solid';
import { IChatMessage, ChatMessageRole, LLMStreamEventType, ChatContextType } from '@packages/global';
import { chatMessageApi, llmApi } from '../../../services/user';
import { useToast } from '../../../stores/toastStore';
import { useChatMessageCacheStore } from '../../../stores/chatMessageCacheStore';

export interface ActionCard {
  type: 'update' | 'add' | 'delete';
  title: string;
  content: string;
  data?: unknown;
}

export interface BaseStepChatPanelProps {
  projectId: string;
  episodeScriptId: string;
  stepNumber: number;
  stepName: string;
  contextType: ChatContextType;
  currentStepData?: unknown;
  onApplyAction?: (action: ActionCard) => void;
  parseResponse?: (content: string) => { message: string; actions: ActionCard[] };
}

export default function BaseStepChatPanel({
  projectId,
  episodeScriptId,
  stepNumber,
  stepName,
  contextType,
  currentStepData,
  onApplyAction,
  parseResponse,
}: BaseStepChatPanelProps) {
  const toast = useToast();
  const { t } = useTranslation(['script', 'common']);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 对于脚本步骤上下文，使用 episodeScriptId 作为 contextId
  // 后端 LLMController 会根据 contextType 判断 projectId 实际是 scriptId
  const effectiveProjectId = episodeScriptId;

  // 缓存 store - 使用稳定引用避免 useCallback 依赖变化
  const cacheStore = useChatMessageCacheStore;

  // 防止并发请求（React StrictMode 双重渲染时第二次请求在第一次完成前发出）
  const loadingRef = useRef(false);

  const loadMessages = useCallback(async () => {
    // 优先从缓存读取
    const cached = cacheStore.getState().getCached(effectiveProjectId, contextType);
    if (cached) {
      setMessages(cached);
      return;
    }
    // 防止并发重复请求
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setLoading(true);
      const data = await chatMessageApi.list({ projectId: effectiveProjectId, contextType, limit: 50 });
      setMessages(data);
      cacheStore.getState().setCache(effectiveProjectId, contextType, data);
    } catch (error) {
      console.error('加载消息失败:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [effectiveProjectId, contextType]);

  useEffect(() => {
    loadMessages();
    return () => { abortControllerRef.current?.abort(); };
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingContent]);

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userMessage = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setStreaming(true);
    setStreamingContent('');

    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`,
      projectId: effectiveProjectId,
      contextType,
      role: ChatMessageRole.USER,
      content: userMessage,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    cacheStore.getState().appendMessage(effectiveProjectId, contextType, tempUserMsg);

    try {
      abortControllerRef.current = new AbortController();
      await llmApi.stream(
        { projectId: effectiveProjectId, contextType, prompt: userMessage, currentContent: JSON.stringify(currentStepData) },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`,
                projectId: effectiveProjectId,
                contextType,
                role: ChatMessageRole.ASSISTANT,
                content: event.content || '',
                createdAt: new Date(),
                updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);
              cacheStore.getState().appendMessage(effectiveProjectId, contextType, aiMsg);
              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('chatPanel.aiFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: unknown) {
      if ((error as Error).name !== 'AbortError') {
        toast.error(t('chatPanel.sendFailed'));
      }
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    if (!confirm(t('chatPanel.clearConfirm'))) return;
    try {
      await chatMessageApi.clear(effectiveProjectId, contextType);
      setMessages([]);
      cacheStore.getState().clearCache(effectiveProjectId, contextType);
      toast.success(t('chatPanel.cleared'));
    } catch {
      toast.error(t('chatPanel.clearFailed'));
    }
  };

  // 删除单条消息
  const handleDeleteMessage = async (messageId: string) => {
    // 临时消息直接从本地删除
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      cacheStore.getState().removeMessage(effectiveProjectId, contextType, messageId);
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      cacheStore.getState().removeMessage(effectiveProjectId, contextType, messageId);
    } catch (error) {
      console.error('删除消息失败:', error);
      toast.error(t('chatPanel.deleteFailed'));
    }
  };

  const handleApply = (action: ActionCard) => {
    onApplyAction?.(action);
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const defaultParseResponse = (content: string): { message: string; actions: ActionCard[] } => {
    try {
      // 1. 尝试匹配 markdown 代码块中的 JSON
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]);
        return { message: parsed.message || '', actions: parsed.actions || (parsed.content ? [{ type: 'update', title: stepName, content: parsed.content, data: parsed.content }] : []) };
      }

      // 2. 尝试直接解析为 JSON
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed);
        return { message: parsed.message || '', actions: parsed.actions || (parsed.content ? [{ type: 'update', title: stepName, content: typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content), data: parsed.content }] : []) };
      }

      // 3. 尝试提取 JSON 对象
      const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
      if (jsonObjectMatch) {
        const parsed = JSON.parse(jsonObjectMatch[0]);
        return { message: parsed.message || '', actions: parsed.actions || (parsed.content ? [{ type: 'update', title: stepName, content: typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content), data: parsed.content }] : []) };
      }

      return { message: content, actions: [] };
    } catch {
      return { message: content, actions: [] };
    }
  };

  const parse = parseResponse || defaultParseResponse;

  return (
    <div className="w-full h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('chatPanel.title')}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button onClick={handleClear} className="p-1.5 text-gray-400 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.clearChat')}>
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-success/10 rounded">
            <div className="w-1.5 h-1.5 bg-success rounded-full" />
            <span className="text-[11px] text-success font-medium">{t('chatPanel.online')}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400"><span className="text-sm">{t('loading')}</span></div>
        ) : (
          <>
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">{t('chatPanel.welcomeMessage')}</p>
              </div>
            </div>

            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { message, actions } = isUser ? { message: msg.content, actions: [] } : parse(msg.content);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div className={`rounded-xl px-3 py-2.5 ${isUser ? 'bg-primary/20 border border-primary' : 'bg-bg-elevated'}`}>
                        <p className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'}`}>
                          {message.length > 300 ? message.slice(0, 300) + '...' : message}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>{formatTime(msg.createdAt)}</span>
                        {isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isUser && actions.map((action, idx) => {
                    const typeLabels = { update: t('action.edit'), add: t('action.add'), delete: t('action.delete') };
                    const typeColors = { update: 'text-primary', add: 'text-success', delete: 'text-error' };
                    return (
                      <div key={idx} className="ml-9">
                        <div className="rounded-lg border p-3 bg-bg-surface border-border-default">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-medium ${typeColors[action.type]}`}>{typeLabels[action.type]}：{action.title}</span>
                            <button onClick={() => handleApply(action)} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-success text-success hover:bg-success/10 transition-colors cursor-pointer">
                              <CheckIcon className="w-3 h-3" />{t('chatPanel.apply')}
                            </button>
                          </div>
                          <p className="text-xs leading-relaxed text-gray-200">{action.content}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{streamingContent}</p>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1 rounded-lg bg-bg-elevated">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('chatPanel.inputPlaceholder')}
            disabled={streaming}
            rows={1}
            className="w-full px-3.5 py-2.5 bg-transparent text-[13px] text-gray-50 placeholder-gray-400 outline-none disabled:opacity-50 resize-none overflow-y-auto"
          />
        </div>
        <button onClick={handleSend} disabled={!input.trim() || streaming} className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 cursor-pointer">
          <PaperAirplaneIcon className="w-[18px] h-[18px] text-white" />
        </button>
      </div>
    </div>
  );
}
